import React, { useState } from 'react';
import { X, Copy, Check } from 'lucide-react';

function formatRate(rate) {
  if (!rate && rate !== 0) return '—';
  if (rate < 0.01) return `$${(rate * 1000000).toFixed(2)}/1M`;
  return `$${rate.toFixed(2)}/1M`;
}

const tierColors = {
  economy:  { bg: 'rgba(16,185,129,0.08)',  color: '#34D399', border: 'rgba(16,185,129,0.2)' },
  standard: { bg: 'rgba(99,102,241,0.08)',  color: '#818CF8', border: 'rgba(99,102,241,0.2)' },
  premium:  { bg: 'rgba(168,85,247,0.08)',  color: '#C084FC', border: 'rgba(168,85,247,0.2)' },
};

const tierDescriptions = {
  economy:  'Lowest cost. Best-effort routing — may fall back to error if primary provider unavailable.',
  standard: 'Balanced cost and reliability. Automatic failover between providers.',
  premium:  'Highest reliability. Full provider waterfall — maximises uptime.',
};

function CopyBlock({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{
      background: '#0a0a0a', borderRadius: 8, padding: '10px 14px',
      border: '1px solid rgba(255,255,255,0.06)', position: 'relative',
      fontFamily: "'JetBrains Mono', 'Fira Code', monospace", fontSize: 12,
      color: '#E2E8F0', overflowX: 'auto', whiteSpace: 'pre',
    }}>
      {text}
      <button
        onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
        style={{ position: 'absolute', top: 8, right: 8, padding: 4, borderRadius: 4, border: 'none', cursor: 'pointer', background: 'rgba(255,255,255,0.06)', color: copied ? '#34D399' : '#64748B' }}
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
      </button>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
      <span style={{ fontSize: 12, color: '#94A3B8' }}>{label}</span>
      <span style={{ fontSize: 12, color: '#F1F5F9', fontWeight: 500 }}>{value}</span>
    </div>
  );
}

function PricingRows({ pricing }) {
  if (!pricing) return null;
  return (
    <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: 8, padding: '4px 14px', border: '1px solid rgba(255,255,255,0.04)' }}>
      <Row label="Input tokens" value={formatRate(pricing.inputTokenRate)} />
      <Row label="Output tokens" value={formatRate(pricing.outputTokenRate)} />
      {pricing.cachedTokenRate > 0 && <Row label="Cached tokens" value={formatRate(pricing.cachedTokenRate)} />}
      {pricing.contextThreshold && (
        <>
          <Row label={`Input (above ${(pricing.contextThreshold / 1000).toFixed(0)}K)`} value={formatRate(pricing.inputTokenRateAbove)} />
          <Row label={`Output (above ${(pricing.contextThreshold / 1000).toFixed(0)}K)`} value={formatRate(pricing.outputTokenRateAbove)} />
        </>
      )}
    </div>
  );
}

const SectionTitle = ({ children }) => (
  <h3 style={{ fontSize: 12, fontWeight: 600, color: '#94A3B8', margin: '0 0 8px 0', textTransform: 'uppercase', letterSpacing: 0.5 }}>
    {children}
  </h3>
);

export default function ModelDetailsModal({ model, onClose }) {
  if (!model) return null;

  const isEmbedding = model.modelType === 'embedding';
  const endpoint = isEmbedding ? '/llm/v1/embeddings' : '/llm/v1/chat/completions';
  const tierKeys = model.tiers ? Object.keys(model.tiers) : [];
  const hasTiers = tierKeys.length > 0;

  const curlExample = (modelName) => isEmbedding
    ? `curl https://client-api.fluxuro.ai/llm/v1/embeddings \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "${modelName}",
    "input": "Your text to embed"
  }'`
    : `curl https://client-api.fluxuro.ai/llm/v1/chat/completions \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "${modelName}",
    "messages": [{"role": "user", "content": "Hello!"}],
    "stream": true
  }'`;

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: '#0d0d0d', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, width: '100%', maxWidth: 520, maxHeight: '85vh', overflowY: 'auto', padding: 24 }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 600, color: '#F1F5F9', margin: 0 }}>
              {model.displayName || model.name}
            </h2>
            <p style={{ fontSize: 13, color: '#64748B', margin: '4px 0 0 0' }}>
              {model.family} · {isEmbedding ? 'Embedding model' : 'Chat model'}
              {model.contextWindow ? ` · ${(model.contextWindow / 1000).toFixed(0)}K context` : ''}
            </p>
          </div>
          <button onClick={onClose} style={{ padding: 6, borderRadius: 6, border: 'none', cursor: 'pointer', background: 'rgba(255,255,255,0.06)', color: '#94A3B8' }}>
            <X size={16} />
          </button>
        </div>

        {/* Model ID */}
        <div style={{ marginBottom: 20 }}>
          <SectionTitle>Model ID</SectionTitle>
          <CopyBlock text={model.name} />
          {model.aliases?.length > 0 && (
            <p style={{ fontSize: 11, color: '#64748B', margin: '6px 0 0 0' }}>Aliases: {model.aliases.join(', ')}</p>
          )}
        </div>

        {/* Endpoint */}
        <div style={{ marginBottom: 20 }}>
          <SectionTitle>Endpoint</SectionTitle>
          <CopyBlock text={`POST https://client-api.fluxuro.ai${endpoint}`} />
        </div>

        {/* Pricing — standard (no tiers) */}
        {model.pricing && !hasTiers && (
          <div style={{ marginBottom: 20 }}>
            <SectionTitle>Pricing</SectionTitle>
            <PricingRows pricing={model.pricing} />
          </div>
        )}

        {/* Tier Pricing */}
        {hasTiers && (
          <div style={{ marginBottom: 20 }}>
            <SectionTitle>Tier Pricing</SectionTitle>
            <p style={{ fontSize: 12, color: '#64748B', margin: '0 0 12px 0' }}>
              Append a tier suffix to the model name to control cost vs reliability.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {/* Default pricing if exists */}
              {model.pricing && (
                <div style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#94A3B8' }}>Default</span>
                    <span style={{ fontSize: 11, color: '#64748B' }}>{model.name}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 16 }}>
                    <span style={{ fontSize: 12, color: '#94A3B8' }}>In: <span style={{ color: '#F1F5F9' }}>{formatRate(model.pricing.inputTokenRate)}</span></span>
                    <span style={{ fontSize: 12, color: '#94A3B8' }}>Out: <span style={{ color: '#F1F5F9' }}>{formatRate(model.pricing.outputTokenRate)}</span></span>
                  </div>
                </div>
              )}
              {tierKeys.map(tier => {
                const tc = tierColors[tier] || {};
                const tierPricing = model.tiers[tier];
                return (
                  <div key={tier} style={{ padding: '10px 14px', borderRadius: 8, border: `1px solid ${tc.border || 'rgba(255,255,255,0.06)'}`, background: tc.bg || 'rgba(255,255,255,0.02)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: tc.color || '#94A3B8', textTransform: 'capitalize' }}>{tier}</span>
                      <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#64748B' }}>{model.name}-{tier}</span>
                    </div>
                    <p style={{ fontSize: 11, color: '#64748B', margin: '0 0 6px 0' }}>{tierDescriptions[tier]}</p>
                    <div style={{ display: 'flex', gap: 16 }}>
                      <span style={{ fontSize: 12, color: '#94A3B8' }}>In: <span style={{ color: tc.color || '#F1F5F9' }}>{formatRate(tierPricing?.inputTokenRate)}</span></span>
                      <span style={{ fontSize: 12, color: '#94A3B8' }}>Out: <span style={{ color: tc.color || '#F1F5F9' }}>{formatRate(tierPricing?.outputTokenRate)}</span></span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Example */}
        <div>
          <SectionTitle>Example</SectionTitle>
          <CopyBlock text={curlExample(hasTiers ? `${model.name}-${tierKeys[0]}` : model.name)} />
          {hasTiers && (
            <p style={{ fontSize: 11, color: '#64748B', margin: '6px 0 0 0' }}>
              Replace <span style={{ fontFamily: 'monospace', color: '#94A3B8' }}>-{tierKeys[0]}</span> with any available tier suffix.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
